import React, { useState, useEffect } from "react";
import Chatbot from "react-chatbot-kit";
import "react-chatbot-kit/build/main.css";
import MessageParser from "./MessageParser";
import ActionProvider from "./ActionProvider";
import config from "./chatbotConfig";
import "./acubot.css";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Acubot = () => {
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [showLauncherIcon, setShowLauncherIcon] = useState(true);
  const [isLauncherMinimized, setIsLauncherMinimized] = useState(false);

  const defaultChatWindowPosition = { bottom: 80, right: 16 };
  const [chatWindowPosition, setChatWindowPosition] = useState(
    defaultChatWindowPosition,
  );

  const saveMessages = (messages) => {
    sessionStorage.setItem("chat_messages", JSON.stringify(messages));
  };

  const loadMessages = () => {
    const messages = sessionStorage.getItem("chat_messages");
    return messages ? JSON.parse(messages) : undefined;
  };

  useEffect(() => {
    if (showChatWindow && !isLauncherMinimized) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showChatWindow, isLauncherMinimized]);

  // Global control API
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.acubot = {
        openChatWindow: () => setShowChatWindow(true),
        closeChatWindow: () => setShowChatWindow(false),
        toggleChatWindow: () => setShowChatWindow((prev) => !prev),
        isChatOpenWindow: () => showChatWindow,

        showLauncher: () => setShowLauncherIcon(true),
        hideLauncher: () => setShowLauncherIcon(false),
        toggleLauncher: () => setShowLauncherIcon((prev) => !prev),
        isLauncherVisible: () => showLauncherIcon,

        minimizeLauncher: () => setIsLauncherMinimized(true),
        maximizeLauncher: () => setIsLauncherMinimized(false),
        isLauncherMinimized: () => isLauncherMinimized,

        setChatWindowPosition: (pos) => {
          setChatWindowPosition((prev) => ({ ...prev, ...pos }));
        },
      };
    }
  }, [showChatWindow, isLauncherMinimized, showLauncherIcon]);

  const location = useLocation();

  useEffect(() => {
    // Reset on route change
    setShowLauncherIcon(true);
    setShowChatWindow(false);
    setChatWindowPosition(defaultChatWindowPosition);
  }, [location.pathname]);

  return (
    <>
      <AnimatePresence>
        {showChatWindow && !isLauncherMinimized && (
          <>
            <motion.div
              key="overlay"
              className="fixed inset-0 bg-opacity-30 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setShowChatWindow(false)}
            />

            <motion.div
              key="chat-popup"
              style={{ ...chatWindowPosition, position: "fixed", zIndex: 50 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Chatbot
                config={{
                  ...config,
                  customComponents: {
                    ...config.customComponents,
                    header: () =>
                      config.customComponents.header({
                        onClose: () => setShowChatWindow(false),
                      }),
                  },
                }}
                placeholderText="Type your message here..."
                actionProvider={ActionProvider}
                messageHistory={loadMessages()}
                messageParser={MessageParser}
                saveMessages={saveMessages}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {showLauncherIcon && (
        <motion.button
          onClick={() => {
            setShowChatWindow((prev) => !prev);
            setIsLauncherMinimized(false);
            // Optional gtag tracking
            if (typeof window !== "undefined" && window.gtag) {
              window.gtag("event", "acubot_opened", {
                event_category: "engagement",
                event_label: `Acubot Chat Opened at ${new Date().toISOString()}`,
                value: 1,
              });
            }
          }}
          id="acubot-button"
          className={clsx(
            "fixed bottom-4 right-4 z-20 rounded-full bg-blue-200 shadow flex items-center justify-center transition-all duration-300",
            isLauncherMinimized ? "w-8 h-8" : "w-16 h-16",
          )}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <img
            src={
              "https://acuvisor.s3.ap-south-1.amazonaws.com/dev/Images/Icons/acubot-bunny.svg"
            }
            alt="Bot Avatar"
            style={{ width: "100%", height: "100%" }}
          />
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsLauncherMinimized((prev) => !prev);
            }}
            className="absolute -top-4 -right-2 w-5 h-5 flex items-center justify-center"
          >
            {isLauncherMinimized ? (
              <ChevronUp size={10} />
            ) : (
              <ChevronDown size={12} />
            )}
          </div>
        </motion.button>
      )}
    </>
  );
};

export default Acubot;
